import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
    ChevronLeft,
    ChevronRight,
    ChevronDown,
    Contact,
    FileBadge,
    CalendarCheck2,
    LayoutDashboard,
    Users,
    FolderKanban,
    Camera,
    Inbox,
    Activity,
    History,
    Clock,
    Settings,
    LogOut,
    ScrollText,
    ShieldCheck,
    Bell,
    Wallet,
    Send,
    Server,
    Upload,
    Printer,
    FileDown,
    AlertTriangle,
    FileSignature,
    FileSearch,
    Mail,
    UserCircle,
    ClipboardCheck,
    Archive,
    BookCheck,
    MapPin,
    CalendarDays,
    CalendarRange,
    Plane,
    Zap,
    Link2,
    Banknote,
    Gift,
    BookOpen,
    UserCheck,
    PiggyBank,
    CalendarOff,
} from "lucide-react";

/**
 * Sidebar sections — each item can be:
 *   { path, icon, label, [end] }                 → NavLink (route-based, with active highlight)
 *   { path, action, icon, label }                → Link to `${path}?action=...` (transient action,
 *                                                   the Dashboard handles the action via useSearchParams)
 *   { onClick, icon, label }                     → plain button (opens a global dialog)
 *   { ..., future: true }                        → shown but dimmed ("soon" badge)
 *   { ..., staff: true }                         → visible also to operators (employees promoted
 *                                                   to "amministratore": employees + documents)
 */
const buildSections = (callbacks, role, operatorPages) => {
    const sections = [
        {
            title: "Panoramica",
            icon: LayoutDashboard,
            items: [
                { path: "/", icon: LayoutDashboard, label: "Dashboard", end: true, staff: true },
                { path: "/scadenze", icon: AlertTriangle, label: "Scadenze", staff: true },
                { path: "/conformita", icon: ShieldCheck, label: "Conformità HR" },
                { path: "/impostazioni/consegna", icon: Send, label: "Consegna telematica" },
                { path: "/registro-email", icon: Mail, label: "Registro Email", staff: true },
            ],
        },
        {
            title: "Personale",
            icon: Users,
            items: [
                { path: "/dipendenti", icon: Users, label: "Dipendenti", staff: true },
                { path: "/gruppi", icon: FolderKanban, label: "Gruppi", staff: true },
                { path: "/notifiche/modifiche-dati", icon: Bell, label: "Richieste modifiche dati", staff: true },
                { path: "/personale/verifica-date", icon: CalendarCheck2, label: "Verifica date assunzione", staff: true },
                { path: "/dipendenti/attivita", icon: Activity, label: "Registro attività" },
                { path: "/dipendenti", action: "migration", icon: Server, label: "Migrazione" },
                { path: "/dipendenti", action: "import", icon: Upload, label: "Importa Excel" },
            ],
        },
        {
            title: "Documenti",
            icon: Archive,
            items: [
                { path: "/documenti/archivio", icon: Archive, label: "Archivio", staff: true },
                { path: "/documenti/registro-consegne", icon: BookCheck, label: "Registro consegne", staff: true },
                { path: "/documenti/in-attesa", icon: Inbox, label: "Upload massivo", staff: true },
                { path: "/documenti/da-firmare", icon: FileSignature, label: "Da firmare", staff: true },
                { path: "/documenti/dipendenti", icon: Inbox, label: "Da dipendenti", staff: true },
                { path: "/documenti/mancanti", icon: ClipboardCheck, label: "Documenti mancanti", staff: true },
                { path: "/documenti/contratti", icon: ScrollText, label: "Contratti", staff: true },
                { path: "/documenti/autodichiarazioni", icon: FileBadge, label: "Autodichiarazione emolumenti", staff: true },
                { path: "/documenti/buste-paga", icon: Banknote, label: "Controllo buste paga" },
                { path: "/documenti/benvenuto", icon: Gift, label: "Documenti benvenuto" },
                { path: "/modelli-contratto", icon: FileSignature, label: "Modelli contratto", staff: true },
                { path: "/unilav", icon: FileSearch, label: "UNILAV massivo" },
                { path: "/documenti/storico", icon: ScrollText, label: "Storico operazioni" },
                { path: "/migrazione/stato", icon: Activity, label: "Stato import" },
            ],
        },
        {
            title: "Tesserini",
            icon: Contact,
            items: [
                { path: "/foto/upload", icon: Camera, label: "Upload Fototessere", staff: true },
                { path: "/dipendenti", action: "print-a4", icon: Printer, label: "Stampa A4", staff: true },
                { path: "/dipendenti", action: "export-a4", icon: FileDown, label: "Esporta A4", staff: true },
                { path: "/storico-stampe", icon: History, label: "Storico stampe" },
                { onClick: callbacks?.onOpenSettings, icon: Settings, label: "Impostazioni" },
            ],
        },
        {
            title: "Acconti",
            icon: Wallet,
            items: [
                { path: "/acconti", icon: Wallet, label: "Acconti dipendenti" },
                { path: "/acconti/tfr", icon: PiggyBank, label: "Fondo TFR" },
                { path: "/acconti/fng", icon: CalendarOff, label: "F.N.G. Ferie non godute" },
            ],
        },
        {
            title: "Presenze",
            icon: Clock,
            items: [
                { path: "/presenze", icon: Clock, label: "Timbrature", end: true },
                { path: "/presenze/cantieri", icon: MapPin, label: "Cantieri" },
                { path: "/presenze/assegnazioni", icon: UserCheck, label: "Assegnazioni" },
                { path: "/presenze/pianificazione", icon: CalendarDays, label: "Pianificazione" },
                { path: "/presenze/calendario", icon: CalendarRange, label: "Calendario" },
                { path: "/presenze/ferie", icon: Plane, label: "Ferie e permessi" },
                { path: "/presenze/straordinari", icon: Zap, label: "Straordinari" },
            ],
        },
        {
            title: "Integrazioni",
            icon: Link2,
            items: [
                { path: "/integrazione-rls", icon: Link2, label: "App Straordinari (RLS)" },
            ],
        },
    ];

    if (role !== "operator") return sections;
    const allowed = operatorPages || [];
    const keyOf = (i) => (i.onClick ? "action:settings" : i.action ? `action:${i.action}` : i.path);
    const filtered = sections
        .map((s) => ({ ...s, items: s.items.filter((i) => allowed.includes(keyOf(i))) }))
        .filter((s) => s.items.length > 0);
    filtered.push({
        title: "Area personale",
        icon: UserCircle,
        items: [{ path: "/portale", icon: UserCircle, label: "Il mio portale", staff: true }],
    });
    return filtered;
};

export const Sidebar = ({ open, onToggle, collapsed, onToggleCollapse, onOpenSettings, onOpenGuide, onLogout, user }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const sections = buildSections({ onOpenSettings }, user?.role, user?.operator_pages);

    const isItemActive = (item) => {
        if (item.onClick) return false;             // global dialog triggers — never highlighted
        if (item.action) return false;              // transient action shortcuts — never highlighted
        if (item.end) return location.pathname === item.path;
        return location.pathname.startsWith(item.path);
    };

    const handleActionClick = (item) => {
        if (open) onToggle();
        navigate(`${item.path}?action=${item.action}`);
    };

    // Sezioni a scomparsa: aperta solo quella della pagina corrente; al passaggio del mouse si apre
    // la sezione puntata (accordion) e all'uscita dal menu si torna a quella attiva. In modalità
    // compressa le voci compaiono in un pannello laterale accanto all'icona della sezione.
    const activeTitle = sections.find((s) => s.items.some(isItemActive))?.title || null;
    const [openTitle, setOpenTitle] = useState(activeTitle);
    const [flyTop, setFlyTop] = useState(0);
    useEffect(() => { setOpenTitle(activeTitle); }, [activeTitle, location.pathname]);
    const slug = (t) => t.toLowerCase().replace(/\s+/g, "-");

    const footerName = user?.role === "admin"
        ? "Amministratore"
        : user?.role === "operator"
            ? (user?.name || "Amministratore")
            : user?.cognome || "Utente";

    return (
        <>
            {/* Mobile backdrop */}
            <div
                className={`fixed inset-0 z-30 bg-slate-900/40 transition-opacity lg:hidden ${open ? "opacity-100" : "pointer-events-none opacity-0"}`}
                onClick={onToggle}
                aria-hidden
            />

            {/* Sidebar */}
            <aside
                className={`fixed inset-y-0 left-0 z-40 flex flex-col border-r border-slate-200 bg-white shadow-lg transition-all duration-200
                    ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
                    ${collapsed ? "w-[72px]" : "w-64"}`}
                data-testid="app-sidebar"
            >
                {/* Brand */}
                <div className={`flex items-center border-b border-slate-200 ${collapsed ? "h-16 justify-center px-2" : "min-h-[88px] flex-col justify-center gap-1 px-4 py-3"}`}>
                    {collapsed ? (
                        <img
                            src="/brand/logo-s.jpg"
                            alt="Splendor Sud"
                            className="h-10 w-10 rounded-md object-contain"
                        />
                    ) : (
                        <>
                            <img
                                src="/brand/logo-full.jpg"
                                alt="Splendor Sud"
                                className="h-12 w-full object-contain"
                            />
                            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Portale HR</div>
                        </>
                    )}
                </div>

                {/* Navigation */}
                <nav className="flex-1 overflow-y-auto px-3 py-4" data-testid="sidebar-nav" onMouseLeave={() => setOpenTitle(activeTitle)}>
                    {sections.map((section) => {
                        const SectionIcon = section.icon || LayoutDashboard;
                        const isOpen = openTitle === section.title;
                        const hasActive = section.title === activeTitle;
                        const items = section.items.map((item, idx) => (
                            <SidebarItem
                                key={item.path ? `${item.path}-${item.action || ""}` : `${section.title}-${idx}`}
                                {...item}
                                collapsed={false}
                                active={isItemActive(item)}
                                onNavigate={() => open && onToggle()}
                                onActionClick={() => handleActionClick(item)}
                            />
                        ));
                        const flyHeight = section.items.length * 36 + 44;
                        const top = Math.max(8, Math.min(flyTop, window.innerHeight - flyHeight - 8));
                        return (
                            <div
                                key={section.title}
                                className="mb-1"
                                data-testid={`nav-section-${slug(section.title)}`}
                                onMouseEnter={(e) => { setFlyTop(e.currentTarget.getBoundingClientRect().top); setOpenTitle(section.title); }}
                            >
                                <button
                                    type="button"
                                    onClick={() => setOpenTitle(section.title)}
                                    aria-expanded={isOpen}
                                    title={section.title}
                                    className={`flex w-full items-center gap-3 rounded-md px-2 py-2 text-sm font-semibold transition-colors ${
                                        hasActive ? "text-[#376DB3]" : "text-slate-700"
                                    } ${isOpen && !collapsed ? "bg-slate-50" : "hover:bg-slate-50"} ${collapsed ? "justify-center" : ""}`}
                                    data-testid={`nav-section-toggle-${slug(section.title)}`}
                                >
                                    <span className="relative flex-shrink-0">
                                        <SectionIcon className="h-4 w-4" />
                                        {collapsed && hasActive && <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-[#376DB3]" />}
                                    </span>
                                    {!collapsed && (
                                        <>
                                            <span className="flex-1 truncate text-left">{section.title}</span>
                                            <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                                        </>
                                    )}
                                </button>
                                {isOpen && !collapsed && (
                                    <div className="mb-2 ml-4 mt-0.5 space-y-0.5 border-l border-slate-200 pl-2 animate-in fade-in slide-in-from-top-1 duration-150" data-testid={`nav-section-items-${slug(section.title)}`}>
                                        {items}
                                    </div>
                                )}
                                {isOpen && collapsed && (
                                    <div
                                        className="fixed z-50 w-60 rounded-md border border-slate-200 bg-white p-2 shadow-xl animate-in fade-in slide-in-from-left-1 duration-150"
                                        style={{ left: 72, top }}
                                        data-testid={`nav-section-flyout-${slug(section.title)}`}
                                    >
                                        <div className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-widest text-slate-400">{section.title}</div>
                                        <div className="space-y-0.5">{items}</div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </nav>

                {/* Footer: user info + logout (Impostazioni moved to Tesserini section) */}
                <div className="flex-shrink-0 border-t border-slate-200 p-3">
                    <div className={`rounded-md bg-slate-50 p-2 ${collapsed ? "hidden" : ""}`}>
                        <div className="truncate text-xs font-medium text-slate-700" data-testid="sidebar-user-name">{footerName}</div>
                        {user?.role === "operator" && (
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-[#376DB3]">Amministratore</div>
                        )}
                        <div className="truncate text-[10px] text-slate-500">{user?.email}</div>
                    </div>
                    <button
                        onClick={onOpenGuide}
                        className="mt-2 flex w-full items-center gap-3 rounded-md px-2 py-2 text-sm text-slate-600 hover:bg-slate-100 hover:text-[#376DB3]"
                        title="Guida all'area amministrativa"
                        data-testid="sidebar-guide"
                    >
                        <BookOpen className="h-4 w-4 flex-shrink-0" />
                        <span className={`flex-1 text-left ${collapsed ? "hidden" : ""}`}>Guida</span>
                    </button>
                    <button
                        onClick={onLogout}
                        className="mt-2 flex w-full items-center gap-3 rounded-md px-2 py-2 text-sm text-red-600 hover:bg-red-50"
                        title="Esci"
                        data-testid="sidebar-logout"
                    >
                        <LogOut className="h-4 w-4 flex-shrink-0" />
                        <span className={`flex-1 text-left ${collapsed ? "hidden" : ""}`}>Esci</span>
                    </button>
                </div>

                {/* Collapse toggle (desktop only) */}
                <button
                    onClick={onToggleCollapse}
                    className="absolute -right-3 top-24 hidden h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm hover:text-[#376DB3] lg:flex"
                    title={collapsed ? "Espandi menu" : "Comprimi menu"}
                    data-testid="sidebar-collapse-toggle"
                >
                    {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
                </button>
            </aside>
        </>
    );
};

const SidebarItem = ({
    path,
    action,
    onClick,
    icon: Icon,
    label,
    end,
    future,
    collapsed,
    active,
    onNavigate,
    onActionClick,
}) => {
    const baseClasses = `flex items-center gap-3 rounded-md px-2 py-2 text-sm font-medium transition-colors text-left w-full
        ${active ? "bg-[#376DB3]/10 text-[#376DB3]" : "text-slate-600 hover:bg-slate-50"}
        ${future ? "opacity-60" : ""}`;
    const testId = `nav-${label.toLowerCase().replace(/\s+/g, "-")}`;

    // 1. Pure callback (e.g. open global dialog)
    if (onClick) {
        return (
            <button
                type="button"
                onClick={onClick}
                className={baseClasses}
                title={label}
                data-testid={testId}
            >
                <Icon className="h-4 w-4 flex-shrink-0" />
                <span className={`flex-1 truncate ${collapsed ? "hidden" : ""}`}>{label}</span>
            </button>
        );
    }

    // 2. Action-shortcut (navigates to `${path}?action=...`)
    if (action) {
        return (
            <button
                type="button"
                onClick={onActionClick}
                className={baseClasses}
                title={label}
                data-testid={testId}
            >
                <Icon className="h-4 w-4 flex-shrink-0" />
                <span className={`flex-1 truncate ${collapsed ? "hidden" : ""}`}>{label}</span>
            </button>
        );
    }

    // 3. Regular NavLink
    return (
        <NavLink
            to={path}
            end={!!end || (path === "/")}
            onClick={onNavigate}
            className={baseClasses}
            title={label}
            data-testid={testId}
        >
            <Icon className="h-4 w-4 flex-shrink-0" />
            <span className={`flex-1 truncate ${collapsed ? "hidden" : ""}`}>{label}</span>
            {future && !collapsed && (
                <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-700">
                    soon
                </span>
            )}
        </NavLink>
    );
};
